import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Heart, PartyPopper, ArrowRight } from 'lucide-react';
import { FamilyActivity } from '@/types/familyBonding';

interface WeeklyReviewDialogProps {
  open: boolean;
  onClose: () => void;
  summary: {
    completedCount: number;
    totalCount: number;
    completedActivities: FamilyActivity[];
  };
}

export function WeeklyReviewDialog({
  open,
  onClose,
  summary,
}: WeeklyReviewDialogProps) {
  const hasCompletions = summary.completedCount > 0;
  const completionPercent = summary.totalCount > 0 
    ? Math.round((summary.completedCount / summary.totalCount) * 100) 
    : 0;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {hasCompletions ? (
              <>
                <PartyPopper className="h-5 w-5 text-primary" />
                Great Week!
              </>
            ) : (
              <>
                <Heart className="h-5 w-5 text-primary" />
                New Week Ahead!
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {hasCompletions
              ? `You completed ${summary.completedCount} family activities last week!`
              : "Let's make this week special with your family."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {hasCompletions ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="relative h-24 w-24">
                  <svg className="h-24 w-24 -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      className="text-muted"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${completionPercent * 2.51} 251`}
                      className="text-primary transition-all duration-500"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold">{completionPercent}%</span>
                  </div>
                </div>
              </div>

              <div className="text-center text-sm text-muted-foreground">
                {summary.completedActivities.slice(0, 3).map((a) => (
                  <span key={a.id} className="inline-block px-2 py-1 m-0.5 bg-muted rounded-full text-xs">
                    {a.title}
                  </span>
                ))}
                {summary.completedActivities.length > 3 && (
                  <span className="inline-block px-2 py-1 m-0.5 bg-muted rounded-full text-xs">
                    +{summary.completedActivities.length - 3} more
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <Heart className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                Start bonding with your family this week!
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} className="w-full gap-2">
            Start New Week
            <ArrowRight className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
